import { zodResolver } from "@hookform/resolvers/zod"
import type { FC } from "react"
import { useEffect, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import {
  useAssociatedTestTemplateSelectDialog,
  useCreateAssociatedTestTemplate,
  useUpdateAssociatedTestTemplate,
} from "../../hooks"
import { type AssociatedTestTemplateFormValues, createAssociatedTestTemplateFormSchema } from "../../schemas"
import { getDefaultAssociatedTestTemplateValues, serializeAssociatedTestTemplateForm } from "../../utils"

import AssociatedTestTemplateFormDialog from "./AssociatedTestTemplateFormDialog"
import TestTemplateSelectDialog from "./TestTemplateSelectDialog"

import CancelDialog from "@/components/common/Modals/CancelDialog"
import type { AssociatedTestTemplate } from "@/graphql/codegen/graphql"

enum AssociatedTestTemplateFormStep {
  SelectTestTemplate = "SELECT_TEST_TEMPLATE",
  Form = "FORM",
}

interface AssociatedTestTemplateFormDialogContainerProps {
  productId: number
  associatedTestTemplate: AssociatedTestTemplate | null
  onClose: () => void
  onSubmit: () => void
  companyId?: string | null
  locationId?: string | null
}

export const AssociatedTestTemplateFormDialogContainer: FC<AssociatedTestTemplateFormDialogContainerProps> = ({
  productId,
  associatedTestTemplate,
  onClose,
  onSubmit,
  companyId,
  locationId,
}) => {
  const isCreateMode = !associatedTestTemplate
  const [step, setStep] = useState(
    isCreateMode ? AssociatedTestTemplateFormStep.SelectTestTemplate : AssociatedTestTemplateFormStep.Form
  )
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const { searchQuery, selectedTestTemplate, testTemplates, isFetching, handleSearch, handleTestTemplateSelect } =
    useAssociatedTestTemplateSelectDialog({ productId, companyId, locationId })
  const currentTestTemplate = associatedTestTemplate?.testTemplate ?? selectedTestTemplate
  const formSchema = useMemo(
    () => createAssociatedTestTemplateFormSchema(currentTestTemplate?.comparison),
    [currentTestTemplate]
  )
  const methods = useForm<AssociatedTestTemplateFormValues>({
    defaultValues: getDefaultAssociatedTestTemplateValues(associatedTestTemplate),
    resolver: zodResolver(formSchema),
    mode: "all",
  })
  const {
    formState: { isDirty, isValid, isSubmitting },
    handleSubmit,
    reset,
    trigger,
  } = methods
  const canSubmit = isDirty && isValid && !isSubmitting
  const { create } = useCreateAssociatedTestTemplate(onSubmit)
  const { update } = useUpdateAssociatedTestTemplate({ onSuccess: onSubmit })

  useEffect(() => reset(), [selectedTestTemplate, reset])

  const handleClose = () => {
    if (selectedTestTemplate || isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }

  const handleCancelDialogClose = () => {
    setIsCancelDialogOpen(false)
  }

  const handleCancelConfirm = () => {
    onClose()
  }

  const handleContinue = async () => {
    setStep(AssociatedTestTemplateFormStep.Form)
  }

  const handleGoBack = () => {
    setStep(AssociatedTestTemplateFormStep.SelectTestTemplate)
  }

  const submitForm = async (formValues: AssociatedTestTemplateFormValues) => {
    const payload = serializeAssociatedTestTemplateForm(formValues, currentTestTemplate!.comparison)

    if (isCreateMode) {
      await create({
        productId,
        testTemplateId: selectedTestTemplate!.testTemplateId,
        ...(companyId && { customerId: companyId }),
        ...(locationId && { locationId }),
        ...payload,
      })
    } else {
      await update({
        associatedTestTemplateId: associatedTestTemplate.associatedTestTemplateId,
        ...payload,
      })
    }
  }

  useEffect(() => {
    if (!isCreateMode) {
      trigger("minimumValue")
      trigger("maximumValue")
    }
  }, [isCreateMode, trigger])

  return isCancelDialogOpen ? (
    <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleCancelConfirm} />
  ) : (
    <>
      {step === AssociatedTestTemplateFormStep.SelectTestTemplate && (
        <TestTemplateSelectDialog
          productId={productId}
          onClose={handleClose}
          searchQuery={searchQuery}
          selectedTestTemplate={selectedTestTemplate}
          onTestTemplateSelect={handleTestTemplateSelect}
          onSearch={handleSearch}
          testTemplates={testTemplates}
          isFetching={isFetching}
          onContinue={handleContinue}
        />
      )}
      {step === AssociatedTestTemplateFormStep.Form && (
        <FormProvider {...methods}>
          <AssociatedTestTemplateFormDialog
            isCreateMode={isCreateMode}
            testTemplate={currentTestTemplate!}
            canSubmit={canSubmit}
            onClose={handleClose}
            onGoBack={handleGoBack}
            onSubmit={handleSubmit(submitForm)}
          />
        </FormProvider>
      )}
    </>
  )
}
